import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Skeleton, Text, UnstyledButton } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useStudy } from '../app/study';
import { api } from '../lib/api';
import { startActivity, type ActivityHandle } from '../lib/session';
import { fmtTime } from '../lib/text';
import { ttsCalmLine, ttsPlayer, useTtsKeyStatus } from '../lib/tts-player';
import {
  currentLine,
  currentUrl,
  ended,
  halt,
  initQueue,
  markReady,
  queueProgress,
  skipNext,
  skipPrev,
  start as startQueue,
  toPrefetch,
  type QueueState,
} from '../lib/tts-queue';
import { IconBack, IconNext, IconPause, IconPlay, IconPrev } from '../components/watch/icons';
import {
  exposuresForLine,
  linesInSpan,
  listenKey,
  parseSpan,
  replayForLine,
  spanBound,
  toQueueLines,
} from './listen-behavior';

/**
 * The Listen tile (M6a step 12) — `/listen?video&startMs&endMs`, design `3a`: "ears only, for
 * the commute".
 *
 * A span of a scene, spoken line after line by the TTS voice, with the Georgian line on screen
 * small and no English anywhere. That is the whole screen: the ear only does the work when the
 * eye cannot do it for them, and every affordance that would let the eye help — the translation,
 * the reveal, the gloss — is absent by construction rather than merely turned off.
 *
 * Three modules do the work and this file wires them together:
 *
 *  - `lib/tts-queue.ts` is the walk — which line is current, what to synthesize next, and the
 *    one distinction the screen turns on: **advance on `ended`, halt on `stop`**. It is pure and
 *    tested; nothing here reimplements it.
 *  - `lib/tts-player.ts` is the one audio element in the app, and its `onEnded` edge is the only
 *    thing this screen chains on. The now-playing bar's ✕ (mounted app-wide in `app/Shell.tsx`,
 *    so it IS on screen here) and the lock screen's stop button both call `ttsPlayer.stop()`,
 *    which the ordinary subscription cannot tell from a finished clip — chaining on that would
 *    restart audio the learner has just turned off and cannot get rid of.
 *  - `screens/listen-behavior.ts` is the span filter and the two event payloads, so both can be
 *    tested without a DOM.
 *
 * Media Session comes free: the player claims the lock screen as the 'tts' owner, so ⏯ and stop
 * on a locked phone already work and this screen must not claim it a second time.
 */

/** The bar is 44px + 12px of margin and sits over the content; the controls must clear it. */
const BAR_CLEARANCE = 76;

export function Listen() {
  const [params] = useSearchParams();
  const study = useStudy();
  const navigate = useNavigate();

  // Read once, at mount. Re-reading the query string mid-scene would rebuild the queue and start
  // the span again from the top, which is the one thing a listener will not forgive.
  const [scope] = useState(() => ({
    videoId: params.get('video') ?? '',
    span: parseSpan(params),
    dailyBiteId: params.get('dailyBiteId') ?? undefined,
    // Finite or absent, the same rule `spanBound` applies to the span: `planIdx` is sent to
    // `activityStart` as a number, and a NaN from a mangled link serialises as `null` — a body
    // the route rejects, which would cost the whole activity rather than just the bite's grip.
    // (`Number('0')` is 0 and `params.get('0')` is the truthy string "0", so tile 0 is kept.)
    planIdx: spanBound(params.get('planIdx')),
  }));

  const targetLang = study.me.profiles[0]?.targetLang ?? 'ka';
  const video = useQuery({
    queryKey: ['video', scope.videoId],
    queryFn: () => api.videoGet(scope.videoId),
    enabled: scope.videoId !== '',
  });

  const lines = useMemo(
    () => toQueueLines(linesInSpan(video.data?.lines ?? [], scope.span)),
    [video.data, scope.span],
  );

  const [queue, setQueue] = useState<QueueState | null>(null);
  useEffect(() => {
    // Built once, from the first answer. A background refetch (react-query revalidates on focus,
    // and a phone unlocking mid-scene is a focus) hands back a new array of the same lines, and
    // rebuilding on that would drop the learner back at line one halfway through the commute.
    setQueue((prev) => prev ?? (video.data ? initQueue(lines) : null));
  }, [video.data, lines]);

  const [activityId, setActivityId] = useState<string | null>(null);
  const [calm, setCalm] = useState<string | null>(null);

  // ------------------------------------------------ playing time
  /**
   * `playedMs` is time the voice was actually SOUNDING, which on this screen is the only honest
   * measure of the minute: a queue can sit halted at a red light for ten minutes. Accumulated
   * from the player's own transitions rather than from wall clock between start and unmount.
   */
  const clock = useRef({ total: 0, since: null as number | null });
  const playedMs = useCallback(
    () => clock.current.total + (clock.current.since ? Date.now() - clock.current.since : 0),
    [],
  );
  useEffect(
    () =>
      ttsPlayer.subscribe((s) => {
        const c = clock.current;
        if (s.status === 'playing') {
          if (c.since === null) c.since = Date.now();
        } else if (c.since !== null) {
          c.total += Date.now() - c.since;
          c.since = null;
        }
      }),
    [],
  );

  // ------------------------------------------------ the activity
  const activityRef = useRef<ActivityHandle | null>(null);
  useEffect(() => {
    if (!scope.videoId) return;
    let cancelled = false;
    let handle: ActivityHandle | null = null;
    void (async () => {
      try {
        const a = await startActivity(
          study.session,
          'listen',
          {
            videoId: scope.videoId,
            ...(scope.span.startMs !== undefined ? { startMs: scope.span.startMs } : {}),
            ...(scope.span.endMs !== undefined ? { endMs: scope.span.endMs } : {}),
          },
          {
            ...(scope.dailyBiteId !== undefined && scope.planIdx !== undefined
              ? { dailyBiteId: scope.dailyBiteId, planIdx: scope.planIdx }
              : {}),
          },
        );
        if (cancelled) {
          void a.end();
          return;
        }
        handle = a;
        activityRef.current = a;
        setActivityId(a.id);
      } catch (err) {
        // The same call M2 made on Watch: listening is more important than the bookkeeping, so
        // the scene plays on and its events carry `activityId: null`.
        console.warn('[listen] activityStart failed; events carry activityId null', err);
      }
    })();
    return () => {
      cancelled = true;
      activityRef.current = null;
      if (handle) void handle.end({ playedMs: playedMs() });
    };
  }, [scope, study.session, playedMs]);

  // The tab closing mid-scene still ends the activity, so the minute counts (M5's "it all
  // counted"). Keepalive: the request has to outlive the page.
  useEffect(() => {
    const onHide = () => {
      void activityRef.current?.end({ playedMs: playedMs(), keepalive: true });
    };
    window.addEventListener('pagehide', onHide);
    return () => window.removeEventListener('pagehide', onHide);
  }, [playedMs]);

  // ------------------------------------------------ synthesis
  /**
   * Ask the server for the current line and the next few (`toPrefetch`). The server caches the
   * audio in R2 by text+voice and the service worker caches the URL, so the second listen of a
   * scene costs nothing and works with no connection at all — which is what `3a`'s "audio
   * already downloaded" promises.
   */
  const requested = useRef(new Set<string>());
  useEffect(() => {
    if (!queue) return;
    // A halted queue asks for nothing. Without this the failure path below would be a loop: the
    // catch returns the line's id to the pool and halts, halting re-runs this effect, and the
    // effect asks again — an unentitled learner would hammer the route until they navigated away.
    // Idle is NOT halted: the first few lines are fetched before the first tap, which is what
    // makes that tap instant.
    if (queue.status === 'halted' || queue.status === 'done') return;
    for (const line of toPrefetch(queue)) {
      if (requested.current.has(line.lineId)) continue;
      requested.current.add(line.lineId);
      void api
        .tts({ targetLang, text: line.text })
        .then((audio) => setQueue((s) => (s ? markReady(s, line.lineId, audio.url) : s)))
        .catch((err) => {
          // Every refusal this route can give — 403 unentitled, 429 the day's budget, 503 no
          // voice configured — is a fact about the voice, not about this line, so grinding on to
          // the next forty lines would produce forty identical failures. Halt and say it once;
          // ▶ retries, because the id comes back out of the set.
          requested.current.delete(line.lineId);
          setCalm(ttsCalmLine(err));
          setQueue((s) => (s ? halt(s) : s));
        });
    }
  }, [queue, targetLang]);

  // ------------------------------------------------ playing, and the events playing writes
  /** the clip the play effect last started, so a re-render never restarts the line under itself */
  const startedKey = useRef<string | null>(null);
  const emit = study.emit;
  const showTitle = video.data?.title ?? null;
  useEffect(() => {
    if (!queue || queue.status !== 'playing') return;
    const line = currentLine(queue);
    const url = currentUrl(queue);
    if (!line || !url) return; // still synthesizing — the effect runs again on markReady
    const key = listenKey(line.lineId);
    if (startedKey.current === key) return;
    startedKey.current = key;
    void ttsPlayer.play({
      url,
      title: line.text,
      subtitle: showTitle ? `${showTitle} · ears only` : 'ears only',
      key,
    });
    // As the line PLAYS, not when it was fetched: an `exposure` per word on the listen axis,
    // flagged `subtitlesHidden`. See `listen-behavior.ts` for why that flag is honest here.
    //
    // No `document.visibilityState` gate, and that is the difference from Watch: a video playing
    // in a hidden tab is not being watched, whereas a scene playing to a pocket is exactly what
    // this screen is for. Gating here would throw away the commute — the data the listen axis
    // has been waiting for since M4.
    for (const e of exposuresForLine(line, { activityId, videoId: scope.videoId })) emit(e);
  }, [queue, emit, activityId, scope.videoId, showTitle]);

  // The one edge that may advance the queue. `finished.key` is checked because the player is
  // shared: a gloss sheet's "Hear it" ending must not step this scene forward.
  useEffect(
    () =>
      ttsPlayer.onEnded((finished) => {
        if (!finished?.key?.startsWith('listen:')) return;
        setQueue((s) => (s ? ended(s) : s));
      }),
    [],
  );

  /**
   * Leaving stops the sound. The queue is this screen's state, so a clip that outlived the
   * screen would play to its end and then fall silent with nothing able to follow it — worse
   * than silence now. Nothing to halt on this path: the queue goes with the component, and the
   * `onEnded` subscription above is torn down in the same pass.
   */
  useEffect(
    () => () => {
      ttsPlayer.stop();
    },
    [],
  );

  const stopHere = useCallback(() => {
    // Halt FIRST. Both calls are ordered against a clip that finishes in the same instant the
    // learner taps: the halt is a functional update, so whichever of it and the `ended` lands
    // first, the queue settles on 'halted' and the play effect never starts the next line.
    setQueue((s) => (s ? halt(s) : s));
    ttsPlayer.stop();
  }, []);

  const play = useCallback(() => {
    setCalm(null);
    // A fresh gesture: let the play effect start the current line again even if it is the one it
    // started last (the ⏮-at-the-first-line case, and the retry after a failed synthesis).
    startedKey.current = null;
    setQueue((s) => (s ? startQueue(s) : s));
  }, []);

  /**
   * "Listen again". A finished queue sits one past its last line, so `start` alone would leave it
   * playing nothing at all — the walk has to go back to the top, and `initQueue` is the only
   * thing allowed to say where the top is. `ready` is carried across: the scene is already
   * synthesized and a second listen must cost nothing, which is what the tile promises.
   */
  const restart = useCallback(() => {
    setCalm(null);
    startedKey.current = null;
    setQueue((s) => (s ? startQueue({ ...initQueue(s.lines), ready: s.ready }) : s));
  }, []);

  const next = useCallback(() => {
    startedKey.current = null;
    setQueue((s) => (s ? skipNext(s) : s));
  }, []);

  const prev = useCallback(() => {
    if (!queue) return;
    startedKey.current = null;
    // The EVENT is computed from the rendered state and emitted outside the setter, because React
    // invokes an updater twice under StrictMode and one tap on ⏮ must write one `replay`, not two.
    const line = currentLine(skipPrev(queue));
    // The app's first LINE-scoped `replay`: asking to hear a line again is evidence about the
    // items in that line, and the fold expands it over them.
    if (line) emit(replayForLine(line, { activityId, videoId: scope.videoId }));
    // The STATE change is functional, over `s` rather than over the captured `queue`. A rendered
    // queue is one commit old, and `skipPrev` spreads the state it is given: writing the captured
    // one back would silently drop any `ready` URL that landed in between — and because the id
    // stays in `requested`, that line would never be asked for again and would wait forever.
    setQueue((s) => (s ? skipPrev(s) : s));
  }, [queue, emit, activityId, scope.videoId]);

  const back = () => {
    stopHere();
    navigate(-1);
  };

  const title = video.data?.title ?? null;
  const spanLabel =
    scope.span.startMs !== undefined && scope.span.endMs !== undefined
      ? `${fmtTime(scope.span.startMs)} – ${fmtTime(scope.span.endMs)}`
      : null;

  if (!scope.videoId) {
    return (
      <Frame onBack={back}>
        <Text fz={13} c="var(--nn-muted)" py="lg">
          Nothing to listen to — open a scene and choose Listen.
        </Text>
      </Frame>
    );
  }
  if (video.isPending) return <ListenSkeleton onBack={back} />;
  if (video.isError) {
    return (
      <Frame onBack={back}>
        <Text fz={13} c="var(--nn-muted)" py="lg">
          Couldn’t load the scene. It’ll be here when the connection is.
        </Text>
      </Frame>
    );
  }
  if (!queue || queue.lines.length === 0) {
    return (
      <Frame onBack={back} title={title} spanLabel={spanLabel}>
        <Text fz={13} c="var(--nn-muted)" py="lg">
          No lines in this stretch yet — the transcript hasn’t reached it.
        </Text>
      </Frame>
    );
  }

  return (
    <Frame onBack={back} title={title} spanLabel={spanLabel}>
      <Scene
        queue={queue}
        calm={calm}
        onPlay={play}
        onPrev={prev}
        onNext={next}
        onRestart={restart}
        onDone={() => {
          stopHere();
          navigate(-1);
        }}
      />
    </Frame>
  );
}

// ---------------------------------------------------------------------------------------------
// the scene itself
// ---------------------------------------------------------------------------------------------

function Scene({
  queue,
  calm,
  onPlay,
  onPrev,
  onNext,
  onRestart,
  onDone,
}: {
  queue: QueueState;
  calm: string | null;
  onPlay: () => void;
  onPrev: () => void;
  onNext: () => void;
  onRestart: () => void;
  onDone: () => void;
}) {
  const line = currentLine(queue);
  const done = queue.status === 'done';
  // The status of OUR clip, keyed — a primitive snapshot, so the scene does not re-render ten
  // times a second on somebody's `timeupdate`, and a clip from a gloss sheet reads as 'idle'.
  const status = useTtsKeyStatus(line ? listenKey(line.lineId) : '');
  // 'loading' counts as sounding: ⏸ has to be there the moment the tap lands, or the button
  // flickers back to ▶ for the half-second the clip takes to open.
  const sounding = status === 'playing' || status === 'loading';
  const waiting = queue.status === 'playing' && currentUrl(queue) === null;
  /**
   * ⏸ pauses the CLIP and leaves the queue running: the learner is holding the scene, not
   * leaving it, and halting would make ▶ start the line over. ▶ resumes that same paused clip
   * where it can, and otherwise (the bar's ✕, a stop from the lock screen, a failed synthesis)
   * asks the queue to start the current line again.
   */
  const toggle = () => {
    if (sounding) {
      ttsPlayer.pause();
      return;
    }
    if (status === 'paused') {
      ttsPlayer.resume();
      return;
    }
    onPlay();
  };

  return (
    <div style={{ paddingBottom: BAR_CLEARANCE }}>
      <div style={{ font: '10px/1 ui-monospace, Menlo, monospace', color: 'var(--nn-faint)' }}>
        {`LISTEN · ${Math.min(queue.idx + 1, queue.lines.length)} OF ${queue.lines.length}`}
      </div>

      {/* How far through the scene — the only progress this screen shows. */}
      <div
        aria-hidden
        style={{
          marginTop: 9,
          height: 2,
          borderRadius: 2,
          background: 'color-mix(in srgb, var(--nn-text) 10%, transparent)',
        }}
      >
        <div
          style={{
            height: '100%',
            borderRadius: 2,
            width: `${queueProgress(queue) * 100}%`,
            background: 'var(--nn-accent)',
            transition: 'width 220ms linear',
          }}
        />
      </div>

      {/* The line, and nothing else: no translation, no reveal, no tap targets. The eye gets
          enough to know where it is and not one word more. `lang` goes on the Georgian only —
          the closing line is English and mislabelling it would send a screen reader at it in the
          wrong voice. */}
      <div
        style={{
          marginTop: 22,
          minHeight: 78,
          fontSize: 15.5,
          lineHeight: 1.65,
          color: done ? 'var(--nn-muted)' : 'var(--nn-text-strong)',
        }}
      >
        {done ? 'That was the last line.' : <span lang="ka">{line?.text ?? ''}</span>}
      </div>

      {calm && (
        <Text fz={12} c="var(--nn-muted)" mt={2}>
          {calm}
        </Text>
      )}
      {/* Waiting on the voice: a skeleton where the line will be, never a spinner (README §6). */}
      {waiting && !calm && <Skeleton height={10} width="45%" radius="sm" mt={2} />}

      {done ? (
        <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
          <UnstyledButton onClick={onRestart} style={PILL}>
            Listen again
          </UnstyledButton>
          <UnstyledButton onClick={onDone} style={PILL}>
            Done
          </UnstyledButton>
        </div>
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', gap: 20, marginTop: 22 }}>
          <Round onClick={onPrev} label="Previous line">
            <IconPrev />
          </Round>
          <UnstyledButton
            onClick={toggle}
            aria-label={sounding ? 'Pause' : 'Play'}
            style={{
              width: 54,
              height: 54,
              borderRadius: '50%',
              background: 'var(--nn-accent)',
              color: 'var(--nn-on-accent)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <span style={{ display: 'flex' }}>{sounding ? <IconPause /> : <IconPlay />}</span>
          </UnstyledButton>
          <Round onClick={onNext} label="Next line">
            <IconNext />
          </Round>
        </div>
      )}

      <Text fz={11} c="var(--nn-faint)" mt={18}>
        Ears only — there is no English on this screen.
      </Text>
    </div>
  );
}

const PILL = {
  display: 'inline-flex',
  padding: '7px 14px',
  borderRadius: 7,
  border: '1px solid color-mix(in srgb, var(--nn-text) 12%, transparent)',
  fontSize: 12,
  color: 'var(--nn-text)',
} as const;

function Round({
  onClick,
  label,
  children,
}: {
  onClick: () => void;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <UnstyledButton
      onClick={onClick}
      aria-label={label}
      style={{
        width: 44,
        height: 44,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'var(--nn-text)',
        flexShrink: 0,
      }}
    >
      {children}
    </UnstyledButton>
  );
}

function Frame({
  onBack,
  title,
  spanLabel,
  children,
}: {
  onBack: () => void;
  title?: string | null;
  spanLabel?: string | null;
  children: React.ReactNode;
}) {
  return (
    <div style={{ minHeight: '100%' }}>
      <div style={{ display: 'flex', alignItems: 'center', height: 44, padding: '0 4px', gap: 2 }}>
        <UnstyledButton
          onClick={onBack}
          aria-label="Back"
          style={{
            width: 40,
            height: 40,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'var(--nn-text)',
          }}
        >
          <IconBack />
        </UnstyledButton>
        <span style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--nn-text-strong)' }}>
          Listen
        </span>
      </div>
      <div style={{ padding: '0 18px' }}>
        {/* The show's name, not "ep 3, scene 2": series and episodes are M6b, and inventing them
            here would be a promise the data cannot keep. */}
        {(title || spanLabel) && (
          <Text fz={11.5} c="var(--nn-muted)" mb={12} truncate>
            {[title, spanLabel].filter(Boolean).join(' · ')}
          </Text>
        )}
        {children}
      </div>
    </div>
  );
}

/** Skeletons, never spinners (design README §6). */
function ListenSkeleton({ onBack }: { onBack: () => void }) {
  return (
    <Frame onBack={onBack}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 8 }}>
        <Skeleton height={10} width="30%" radius="sm" />
        <Skeleton height={2} radius="sm" />
        <Skeleton height={20} width="85%" radius="sm" mt={12} />
        <Skeleton height={44} width={160} radius="xl" mt={14} />
      </div>
    </Frame>
  );
}
